import { combineEpics, ofType } from "redux-observable"
import { from, concat, of, EMPTY } from "rxjs"
import {
    flatMap,
    filter,
    debounceTime,
    catchError,
    switchMap,
} from "rxjs/operators"

import * as actions from "./actions"
import { addErrorNotification } from "../notifications/actions"
import { Epic } from "../../epics"
import { RequestCancelledError } from "../../errors"

export const defaultRequestingConceptsErrorMessage =
    "Something went wrong when fetching concepts."
export const defaultRequestingWorkspacesErrorMessage =
    "Something went wrong when fetching workspaces."

const datakey = "workspace"
const isPrintView = false
// This parameter is null for all GDL requests except for the ones concerning the "datahomefolderfiles"
const currentPath = null

// we have to disable the linting because of way the GDL api
// requires the keys in the filter
const getFilterParams = (filter: string): Record<string, string | boolean> => ({
    gdlcolumn_ScreenLabel: filter,
    canCreateListInWorkspace: true,
    gdlcolumn_IsActive: true,
})

export const initializeConceptsEpics: Epic = (action$, state$, _dependencies) =>
    action$.pipe(
        ofType(actions.INITIALIZE),
        filter(() => !state$.value.conceptsPage.initialized),
        flatMap(() =>
            concat(
                of(actions.requestConcepts()),
                of(actions.requestWorkspaces()),
                of(actions.setInitialized()),
            ),
        ),
    )

export const requestConceptsEpic: Epic = (
    action$,
    _state$,
    { conceptsPage: { conceptsAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_CONCEPTS),
        switchMap(() => {
            return from(conceptsAPI.getConcepts()).pipe(
                flatMap(({ data }) => of(actions.receiveConcepts(data))),
                catchError((error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    console.error(error)
                    return concat(
                        of(actions.receiveConcepts([])),
                        of(
                            addErrorNotification(
                                defaultRequestingConceptsErrorMessage,
                            ),
                        ),
                    )
                }),
            )
        }),
    )

export const filterConceptsEpic: Epic = (
    action$,
    _state$,
    { conceptsPage: { filteringDelay } },
) =>
    action$.pipe(
        ofType(actions.FILTER_CONCEPTS),
        debounceTime(filteringDelay),
        flatMap(() => of(actions.requestConcepts())),
    )

export const requestWorkspacesEpic: Epic = (
    action$,
    state$,
    { conceptsPage: { gdlAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_WORKSPACES),
        switchMap(() => {
            const workspaceInfo = state$.value.conceptsPage.workspacesInfo
            const requestedPageNumber = workspaceInfo.pagination.currentPage
            const filter = getFilterParams(workspaceInfo.filter)

            return from(
                gdlAPI.getGDLData(
                    datakey,
                    requestedPageNumber,
                    filter,
                    isPrintView,
                ),
            ).pipe(
                flatMap(({ pagination, workspaces }) =>
                    of(actions.receiveWorkspaces(pagination, workspaces)),
                ),
                catchError((error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    console.error(error)
                    return concat(
                        of(
                            actions.receiveWorkspaces(
                                {
                                    currentPage: 0,
                                    count: 0,
                                    pageSize: 0,
                                },
                                [],
                            ),
                        ),
                        of(
                            addErrorNotification(
                                defaultRequestingWorkspacesErrorMessage,
                            ),
                        ),
                    )
                }),
            )
        }),
    )

export const filterWorkspacesEpic: Epic = (
    action$,
    state$,
    { conceptsPage: { gdlAPI, filteringDelay } },
) =>
    action$.pipe(
        ofType(actions.FILTER_WORKSPACES),
        debounceTime(filteringDelay),
        switchMap(() => {
            const filter = getFilterParams(
                state$.value.conceptsPage.workspacesInfo.filter,
            )

            return from(
                gdlAPI.applyGDLFilter(datakey, filter, currentPath),
            ).pipe(
                flatMap(({ pagination, workspaces }) =>
                    of(actions.receiveWorkspaces(pagination, workspaces)),
                ),
                catchError((error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    console.error(error)
                    return concat(
                        of(
                            actions.receiveWorkspaces(
                                {
                                    currentPage: 0,
                                    count: 0,
                                    pageSize: 0,
                                },
                                [],
                            ),
                        ),
                        of(
                            addErrorNotification(
                                defaultRequestingWorkspacesErrorMessage,
                            ),
                        ),
                    )
                }),
            )
        }),
    )

export const loadMoreWorkspacesEpic: Epic = (
    action$,
    _state$,
    _dependencies$,
) =>
    action$.pipe(
        ofType(actions.LOAD_MORE_WORKSPACES),
        flatMap(() => of(actions.requestWorkspaces())),
    )

export default combineEpics(
    initializeConceptsEpics,
    requestConceptsEpic,
    filterConceptsEpic,
    requestWorkspacesEpic,
    filterWorkspacesEpic,
    loadMoreWorkspacesEpic,
)
